import React, { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { truncate } from '../../../utils/text-utils';
import { useIntl } from 'react-intl';
import { useLanguage } from '../../../context';
import { getLocalizedPath } from '../../../helpers/routes';
import { getStrapiImageUrl } from '../../../utils/image-utils';
import SkeletonImage from '../../common/SkeletonImage';

const MobileMegaMenu = ({ category, onClose, onDomainChange, showBottomActions = true }) => {
  const location = useLocation();
  const intl = useIntl();
  const { language } = useLanguage();

  const getItemUrl = (item) => {
    const fallbackUrl = getLocalizedPath(
      `/info/${category?.slugFr || category?.slug}/${item.slug}`,
      language
    );
    return item?.link || fallbackUrl;
  };

  // Domaine correspondant à la page actuellement visitée
  const activeDomainFromPath = useMemo(() => {
    if (!category?.submenus || category.submenus.length === 0) return null;
    const match = category.submenus.find((item) => getItemUrl(item) === location.pathname);
    return match ? match.parentDomain : null;
  }, [category, location.pathname, language]);

  const [activeDomain, setActiveDomain] = useState(activeDomainFromPath || null);

  const isCompanies =
    (category?.slug || '').toLowerCase() === 'companies' ||
    (category?.title || '').toLowerCase() === 'companies' ||
    (category?.title || '').toLowerCase() === 'about us' ||
    (category?.title || '').toLowerCase() === 'à propos';

  // Préparer les données avec useMemo pour éviter de recalculer à chaque rendu
  const domainProductMap = useMemo(() => {
    const map = {};

    category.submenus.forEach(item => {
      if (!map[item.parentDomain]) {
        map[item.parentDomain] = [];
      }
      map[item.parentDomain].push(item);
    });

    return map;
  }, [category.submenus]);

  const handleDomainChange = (domain) => {
    setActiveDomain(domain);
    if (onDomainChange) {
      onDomainChange(domain);
    }
  };

  const renderCard = (item, idx) => {
    const fallbackUrl = getLocalizedPath(
      `/info/${category?.slugFr || category?.slug}/${item.slug}`,
      language
    );
    const productUrl = item?.link || fallbackUrl;
    const isActive = location.pathname === productUrl;

    return (
      <NavLink
        key={item.documentId || idx}
        to={productUrl}
        onClick={onClose}
        className={`block border border-slate-200 transition-all duration-200 h-full flex flex-col ${
          isActive
            ? 'border-[#16A34A] bg-[#16A34A]/5'
            : 'hover:border-[#16A34A]/40 hover:shadow-sm'
        }`}
      >
        {item.metaImage && (
          <div className="shrink-0 overflow-hidden">
            <SkeletonImage
              src={getStrapiImageUrl(item.metaImage)}
              alt={item.title}
              className="w-full h-24"
              imgClassName="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-3 flex flex-col flex-grow transition-all duration-200">
          <div className={`font-semibold ${isActive ? 'text-[#15803D]' : 'text-[#0a1628]'}`}>{item.title}</div>
          {item.metaDescription && (
            <div className={`text-xs ${isActive ? 'text-[#16A34A]' : 'text-slate-500'} mt-2`}>{truncate(item.metaDescription, 50)}</div>
          )}
        </div>
      </NavLink>
    );
  };

  return (
    <div className="lg:hidden">
      {isCompanies ? (
        <>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#16A34A]" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#0a1628]">{category?.title}</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {(category?.submenus || []).map((item, idx) => renderCard(item, idx))}
          </div>
        </>
      ) : (
        <>
          {activeDomain ? (
            <>
              <div className="flex justify-between items-center mb-6">
                <button
                  onClick={() => {
                    setActiveDomain(null);
                    onDomainChange?.(null);
                  }}
                  className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 hover:text-[#16A34A]"
                >
                  <ArrowRight className="rotate-180" size={16} />
                  {intl.formatMessage({ id: "menu.backToDomains" })}
                </button>
                <h3 className="text-sm font-bold text-[#0a1628]">{activeDomain}</h3>
                <div className="opacity-0 w-24"></div> {/* Espaceur pour alignement */}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {domainProductMap[activeDomain]?.map((item, idx) => renderCard(item, idx))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-px bg-[#16A34A]" />
                <h3 className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#16A34A]">
                  {intl.formatMessage({ id: "menu.domains", defaultMessage: "Secteurs d'activités" })}
                </h3>
              </div>
              <div className="flex flex-col">
                {Object.keys(domainProductMap).map((domain, idx) => (
                  <button
                    key={idx}
                    className="flex items-center justify-between w-full text-left py-3 border-b border-slate-200 text-[#0a1628] font-semibold hover:text-[#16A34A] transition-colors"
                    onClick={() => {
                      handleDomainChange(domain);
                    }}
                  >
                    <span>{domain}</span>
                    <ArrowRight size={16} />
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Bottom Action Bar */}
      {showBottomActions && (
        <div className="border-t border-slate-200 mt-8 pt-6 flex justify-between items-center">
          <div className="flex space-x-6">
            <a
              href="#"
              className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 hover:text-[#16A34A] transition-colors"
            >
              {intl.formatMessage({ id: "menu.advancedSearch" })}
            </a>
            <a
              href="#"
              className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 hover:text-[#16A34A] transition-colors"
            >
              {intl.formatMessage({ id: "menu.downloadCatalog" })}
            </a>
          </div>
          <a
            href={getLocalizedPath("/contact", language)}
            className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-[#15803D] text-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200"
          >
            <span>{intl.formatMessage({ id: "menu.contactSales" })}</span>
            <ArrowRight size={16} />
          </a>
        </div>
      )}
    </div>
  );
};

export default MobileMegaMenu;